import { type ComponentType, useState } from "react";
import { File, FileSpreadsheet, FileText, Presentation } from "lucide-react";
import { GenieImageLightbox } from "@/pages/agent-interfaces/live-agent/components/GenieImageLightbox";
import { LINK_ICON_MAP } from "@/pages/agent-interfaces/live-agent/components/infoLinkIcons";
import { cn } from "@/lib/utils";
import type { InfoLink, ResourceDocument } from "@/types/agent/info-links";

const TILE_CLASS = "h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white";
const IMAGE_FILE_NAME = /\.(png|jpe?g|webp|svg|gif)$/i;
const FAVICON_SIZE = 64;

type IconComponent = ComponentType<{ className?: string }>;

const DOCUMENT_ICONS: Record<string, IconComponent> = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  txt: FileText,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  csv: FileSpreadsheet,
  ppt: Presentation,
  pptx: Presentation,
};

const extensionOf = (fileName: string): string => fileName.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? "";

const isImageDocument = (doc: ResourceDocument): boolean =>
  doc.mimeType ? doc.mimeType.startsWith("image/") : IMAGE_FILE_NAME.test(doc.fileName);

const parseAbsoluteUrl = (url: string): URL | null => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
};

interface ThumbProps {
  brand: string;
  className?: string;
}

interface ImageThumbProps {
  src: string;
  title: string;
  className?: string;
  onError: () => void;
}

const ImageThumb = ({ src, title, className, onError }: ImageThumbProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        aria-label={`View ${title} full screen`}
        className={cn(
          TILE_CLASS,
          "cursor-zoom-in transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary",
          className,
        )}
      >
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={onError}
          className="h-full w-full object-cover"
        />
      </button>
      <GenieImageLightbox imageUrl={src} title={title} isOpen={isExpanded} onClose={() => setIsExpanded(false)} />
    </>
  );
};

interface IconTileProps extends ThumbProps {
  icon: IconComponent;
  caption?: string;
}

const IconTile = ({ icon: Icon, caption, brand, className }: IconTileProps) => (
  <div
    aria-hidden
    className={cn(TILE_CLASS, "flex flex-col items-center justify-center gap-0.5", className)}
    style={{ backgroundColor: `${brand}15`, color: brand }}
  >
    <Icon className="h-5 w-5" />
    {caption && <span className="text-[9px] font-semibold uppercase leading-none tracking-wide">{caption}</span>}
  </div>
);

/** A picture's own preview, or an icon for the kind of file it is. */
export const ResourceDocumentThumb = ({ doc, brand, className }: ThumbProps & { doc: ResourceDocument }) => {
  const [failed, setFailed] = useState(false);

  if (isImageDocument(doc) && !failed) {
    return (
      <ImageThumb
        src={doc.fileUrl}
        title={doc.label || doc.fileName}
        className={className}
        onError={() => setFailed(true)}
      />
    );
  }
  const extension = extensionOf(doc.fileName);
  return <IconTile icon={DOCUMENT_ICONS[extension] ?? File} caption={extension} brand={brand} className={className} />;
};

/**
 * A link to a picture previews the picture; any other link shows the site's
 * own icon, falling back to the link's icon when the site has none.
 */
export const ResourceLinkThumb = ({ link, brand, className }: ThumbProps & { link: InfoLink }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [faviconFailed, setFaviconFailed] = useState(false);
  const url = parseAbsoluteUrl(link.url);

  if (url && IMAGE_FILE_NAME.test(url.pathname) && !imageFailed) {
    return (
      <ImageThumb
        src={link.url}
        title={link.label || link.url}
        className={className}
        onError={() => setImageFailed(true)}
      />
    );
  }

  if (url && !faviconFailed) {
    return (
      <div aria-hidden className={cn(TILE_CLASS, "flex items-center justify-center", className)}>
        <img
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(url.hostname)}&sz=${FAVICON_SIZE}`}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFaviconFailed(true)}
          className="h-6 w-6 rounded-sm"
        />
      </div>
    );
  }

  return <IconTile icon={LINK_ICON_MAP[link.icon ?? ""] ?? LINK_ICON_MAP.link} brand={brand} className={className} />;
};
